import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Clock,
  DollarSign,
  TrendingUp,
  Calendar,
  Utensils,
  UserCheck,
  UserX,
  BarChart3,
  Table2,
  Info
} from 'lucide-react';

// Info tooltip component
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block ml-1">
      <Info
        className="h-4 w-4 text-t4 hover:text-blue-600 dark:hover:text-blue-400 cursor-help inline"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute z-50 w-64 p-3 bg-surface-0 text-t1 text-xs rounded-lg shadow-lg border border-line -top-2 left-6">
          {text}
          <div className="absolute left-0 top-3 -ml-2 border-8 border-transparent border-r-slate-900" />
        </div>
      )}
    </div>
  );
};

interface DineInAnalyticsData {
  sessions: {
    total: number;
    completed: number;
    active: number;
    avg_duration_minutes: number;
    avg_guests: number;
  };
  revenue: {
    total: number;
    avg_order_value: number;
    total_orders: number;
    revenue_per_guest: number;
  };
  tables: {
    total: number;
    utilization: {
      table_number: string;
      capacity: number;
      sessions: number;
      hours_used: number;
    }[];
    turnover_rate: number;
  };
  hourly_distribution: {
    hour: number;
    sessions: number;
  }[];
  daily_trend: {
    date: string;
    sessions: number;
    guests: number;
  }[];
  reservations: {
    total: number;
    confirmed: number;
    seated: number;
    cancelled: number;
    no_shows: number;
    avg_party_size: number;
    no_show_rate: number;
  };
  top_waiters: {
    id: number;
    name: string;
    sessions: number;
    orders: number;
    revenue: number;
  }[];
}

interface DineInAnalyticsProps {
  data: DineInAnalyticsData | null;
}

const DineInAnalytics: React.FC<DineInAnalyticsProps> = ({ data }) => {
  const { t } = useTranslation(['analytics', 'common']);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatHour = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  if (!data) {
    return (
      <div className="text-center py-12 text-t4">
        {t('analytics:dineIn.noData')}
      </div>
    );
  }

  // Find peak hour
  const peakHour = data.hourly_distribution.reduce((max, h) =>
    h.sessions > (max?.sessions || 0) ? h : max,
    data.hourly_distribution[0]
  );

  // Calculate max sessions for bar chart scaling
  const maxHourlySessions = Math.max(...data.hourly_distribution.map(h => h.sessions), 1);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <p className="text-sm font-medium text-t3 flex items-center gap-2 mb-2">
            <Utensils className="h-4 w-4" />
            {t('analytics:dineIn.totalSessions')}
            <InfoTooltip text={t('analytics:dineIn.sessionsTooltip')} />
          </p>
          <div className="text-2xl font-bold text-t1">{data.sessions.total}</div>
          <div className="text-sm text-t4 mt-1">
            {t('analytics:dineIn.activeSessions', { count: data.sessions.active })} • {t('analytics:dineIn.completedSessions', { count: data.sessions.completed })}
          </div>
        </div>

        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <p className="text-sm font-medium text-t3 flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4" />
            {t('analytics:dineIn.dineInRevenue')}
            <InfoTooltip text={t('analytics:dineIn.dineInRevenueTooltip')} />
          </p>
          <div className="text-2xl font-bold text-t1">{formatCurrency(data.revenue.total)}</div>
          <div className="text-sm text-t4 mt-1">
            {data.revenue.total_orders} {t('analytics:orders')}
          </div>
        </div>

        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <p className="text-sm font-medium text-t3 flex items-center gap-2 mb-2">
            <Users className="h-4 w-4" />
            {t('analytics:dineIn.avgGuestsPerSession')}
            <InfoTooltip text={t('analytics:dineIn.avgGuestsTooltip')} />
          </p>
          <div className="text-2xl font-bold text-t1">{data.sessions.avg_guests.toFixed(1)}</div>
          <div className="text-sm text-t4 mt-1">
            {t('analytics:dineIn.perGuest', { amount: formatCurrency(data.revenue.revenue_per_guest) })}
          </div>
        </div>

        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <p className="text-sm font-medium text-t3 flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4" />
            {t('analytics:dineIn.avgSessionDuration')}
            <InfoTooltip text={t('analytics:dineIn.avgDurationTooltip')} />
          </p>
          <div className="text-2xl font-bold text-t1">{formatDuration(data.sessions.avg_duration_minutes)}</div>
          <div className="text-sm text-t4 mt-1">
            {t('analytics:dineIn.avgOrder', { amount: formatCurrency(data.revenue.avg_order_value) })}
          </div>
        </div>
      </div>

      {/* Second Row - Table & Reservation Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Table Performance */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Table2 className="h-5 w-5" />
              {t('analytics:dineIn.tablePerformance')}
              <InfoTooltip text={t('analytics:dineIn.tablePerformanceTooltip')} />
            </h3>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-500/10 rounded-lg p-3">
                <div className="text-sm text-blue-600 dark:text-blue-400">{t('analytics:dineIn.totalTables')}</div>
                <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{data.tables.total}</div>
              </div>
              <div className="bg-green-500/10 rounded-lg p-3">
                <div className="text-sm text-green-600 dark:text-green-400">{t('analytics:dineIn.turnoverRate')}</div>
                <div className="text-xl font-bold text-green-700 dark:text-green-300">{data.tables.turnover_rate.toFixed(1)}/day</div>
              </div>
            </div>

            {data.tables.utilization.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-t3 mb-2">{t('analytics:dineIn.topTablesByUsage')}</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {data.tables.utilization.slice(0, 5).map((table, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-surface-2 dark:bg-surface-2/50 rounded">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-t1">{t('analytics:dineIn.tableNumber', { number: table.table_number })}</span>
                        <span className="text-xs text-t4">({t('analytics:dineIn.seats', { count: table.capacity })})</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-t1">{table.sessions}</span>
                        <span className="text-xs text-t4 ml-1">{t('analytics:dineIn.sessions')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reservation Stats */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t('analytics:dineIn.reservationStats')}
              <InfoTooltip text={t('analytics:dineIn.reservationsTooltip')} />
            </h3>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-purple-500/10 rounded-lg p-3">
                <div className="text-sm text-purple-600 dark:text-purple-400">{t('analytics:dineIn.totalReservations')}</div>
                <div className="text-xl font-bold text-purple-700 dark:text-purple-300">{data.reservations.total}</div>
              </div>
              <div className="bg-orange-500/10 rounded-lg p-3">
                <div className="text-sm text-orange-600 dark:text-orange-400">{t('analytics:dineIn.avgPartySize')}</div>
                <div className="text-xl font-bold text-orange-700 dark:text-orange-300">{data.reservations.avg_party_size.toFixed(1)}</div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 mt-4">
              <div className="text-center p-2 bg-green-500/10 rounded">
                <UserCheck className="h-4 w-4 mx-auto text-green-600 dark:text-green-400 mb-1" />
                <div className="text-lg font-bold text-green-700 dark:text-green-300">{data.reservations.seated}</div>
                <div className="text-xs text-green-600 dark:text-green-400">{t('analytics:dineIn.seated')}</div>
              </div>
              <div className="text-center p-2 bg-blue-500/10 rounded">
                <Calendar className="h-4 w-4 mx-auto text-blue-600 dark:text-blue-400 mb-1" />
                <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{data.reservations.confirmed}</div>
                <div className="text-xs text-blue-600 dark:text-blue-400">{t('analytics:dineIn.confirmed')}</div>
              </div>
              <div className="text-center p-2 bg-surface-2 dark:bg-surface-2/50 rounded">
                <UserX className="h-4 w-4 mx-auto text-t3 mb-1" />
                <div className="text-lg font-bold text-t2">{data.reservations.cancelled}</div>
                <div className="text-xs text-t3">{t('analytics:dineIn.cancelled')}</div>
              </div>
              <div className="text-center p-2 bg-red-500/10 rounded">
                <UserX className="h-4 w-4 mx-auto text-red-600 dark:text-red-400 mb-1" />
                <div className="text-lg font-bold text-red-700 dark:text-red-300">{data.reservations.no_shows}</div>
                <div className="text-xs text-red-600 dark:text-red-400">{t('analytics:dineIn.noShows')}</div>
              </div>
            </div>

            {data.reservations.total > 0 && (
              <div className="mt-4 p-3 bg-red-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-red-600 dark:text-red-400">{t('analytics:dineIn.noShowRate')}</span>
                  <span className="font-bold text-red-700 dark:text-red-300">{data.reservations.no_show_rate.toFixed(1)}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hourly Distribution */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        <div className="p-5 border-b border-line">
          <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {t('analytics:dineIn.peakHoursTitle')}
            {peakHour && (
              <span className="text-sm font-normal text-t4 ml-2">
                {t('analytics:dineIn.peakLabel', { hour: formatHour(peakHour.hour), count: peakHour.sessions })}
              </span>
            )}
          </h3>
        </div>
        <div className="p-5">
          <div className="flex items-end gap-1" style={{ height: '128px' }}>
            {Array.from({ length: 24 }, (_, i) => {
              const hourData = data.hourly_distribution.find(h => h.hour === i);
              const sessions = hourData?.sessions || 0;
              const heightPercent = maxHourlySessions > 0 ? (sessions / maxHourlySessions) * 100 : 0;
              const isBusinessHour = i >= 11 && i <= 23;
              const minHeight = sessions > 0 ? 8 : 4;
              const barHeight = Math.max((heightPercent / 100) * 120, minHeight);

              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end" style={{ height: '128px' }}>
                  <div
                    className={`w-full rounded-t transition-all ${
                      sessions > 0
                        ? peakHour?.hour === i
                          ? 'bg-blue-500'
                          : 'bg-blue-400'
                        : isBusinessHour
                          ? 'bg-surface-2'
                          : 'bg-surface-2 dark:bg-surface-2/50'
                    }`}
                    style={{ height: `${barHeight}px` }}
                    title={`${formatHour(i)}: ${sessions} sessions`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-t4 mt-1">
            {Array.from({ length: 24 }, (_, i) => (
              i % 3 === 0 ? <span key={i} className="flex-1 text-center">{i}</span> : <span key={i} className="flex-1"></span>
            ))}
          </div>
          <div className="flex justify-between text-xs text-t4 mt-1">
            <span>00:00</span>
            <span>12:00</span>
            <span>23:00</span>
          </div>
        </div>
      </div>

      {/* Daily Trend & Top Waiters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('analytics:dineIn.dailyTrendLast14')}
            </h3>
          </div>
          <div className="p-5">
            {data.daily_trend.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.daily_trend.map((day, idx) => {
                  const maxSessions = Math.max(...data.daily_trend.map(d => d.sessions), 1);
                  const width = (day.sessions / maxSessions) * 100;

                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-20 text-sm text-t3">
                        {new Date(day.date).toLocaleDateString('el-GR', { weekday: 'short', day: 'numeric' })}
                      </div>
                      <div className="flex-1 bg-surface-2 rounded-full h-6 overflow-hidden">
                        <div
                          className="bg-blue-500 h-full rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.max(width, 10)}%` }}
                        >
                          <span className="text-xs text-t1 font-medium">{day.sessions}</span>
                        </div>
                      </div>
                      <div className="w-16 text-right text-sm text-t4">
                        {day.guests} <Users className="h-3 w-3 inline" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-t4">
                {t('analytics:dineIn.noDataForThisPeriod')}
              </div>
            )}
          </div>
        </div>

        {/* Top Waiters */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('analytics:dineIn.topStaff')}
            </h3>
          </div>
          <div className="p-5">
            {data.top_waiters.length > 0 ? (
              <div className="space-y-3">
                {data.top_waiters.map((waiter, idx) => (
                  <div key={waiter.id} className="flex items-center gap-3 p-3 bg-surface-2 dark:bg-surface-2/50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-t1 font-bold ${
                      idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-orange-400' : 'bg-blue-400'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-t1">{waiter.name}</div>
                      <div className="text-xs text-t4">
                        {t('analytics:dineIn.sessionsAndOrders', { sessions: waiter.sessions, orders: waiter.orders })}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600 dark:text-green-400">{formatCurrency(waiter.revenue)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-t4">
                {t('analytics:dineIn.noStaffData')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DineInAnalytics;
